import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { motion, AnimatePresence } from 'framer-motion'
import { Home, MessageSquare, Bot, Paintbrush, ImageIcon, Menu, Settings } from 'lucide-react'
import { getToken } from '@/utils/request'
import Auth from '@/components/Auth'
import { useModel } from '@/plugin-model/useModel'

// Shadcn UI 组件
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu'
import { cn } from '@/lib/utils'
import IconFont from '@/components/IconFonts'

const HeaderDropdown = dynamic(() => import('@/components/HeaderDropdown'), { ssr: false })

const App = ({ need_login, children }) => {
  const router = useRouter()
  const { view, viewSet } = useModel('view')
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const shouldRedirectLogin = useMemo(() => need_login && !getToken(), [need_login])

  useEffect(() => {
    if (shouldRedirectLogin) {
      router.replace(`/`)
    }
  }, [router, shouldRedirectLogin])

  // 主导航项目
  const mainMenuItems = [
    {
      name: '首页',
      href: '/',
      icon: <Home className="h-4 w-4" />,
      key: 'home',
      description: '探索 Ground AI 的功能和介绍',
    },
    {
      name: '画廊',
      href: '/gallery',
      icon: <ImageIcon className="h-4 w-4" />,
      key: 'gallery',
      description: '浏览社区创作的优质AI作品',
    },
  ]

  // AI聊天子菜单项
  const chatMenuItems = [
    {
      name: 'AI聊天',
      href: '/chat',
      icon: <MessageSquare className="h-4 w-4" />,
      description: '与AI进行流畅对话，获取信息与灵感',
      key: 'chat',
    },
    {
      name: 'AI Bot',
      href: '/chat/bot',
      icon: <Bot className="h-4 w-4" />,
      description: '使用专业定制的AI助手解决特定问题',
      key: 'bot',
    },
  ]

  // 绘画相关的子菜单项目
  const drawingMenuItems = [
    {
      name: 'MJ绘图',
      href: '/creation/mj',
      icon: <IconFont className="h-4 w-4" icon="icon-mj" />,
      key: 'mj',
      description: '使用 Midjourney 创建高质量图像',
    },
    {
      name: 'SD绘图',
      href: '/creation',
      icon: <IconFont className="h-4 w-4" icon="icon-sd" />,
      key: 'sd',
      description: '使用 Stable Diffusion 生成图像',
    },
    {
      name: 'SD3绘图',
      href: '/creation/sd_three',
      icon: <IconFont className="h-4 w-4" icon="icon-sd" />,
      key: 'sd3',
      description: 'Stable Diffusion 3 提供更高质量结果',
    },
    {
      name: '妙笔',
      href: '/creation/magic_quill',
      icon: <IconFont className="h-4 w-4" icon="icon-shufa" />,
      key: 'magic_quill',
      description: '将文字转换为艺术字体',
    },
    {
      name: '高清修复',
      href: '/creation/upscale',
      icon: <IconFont className="h-4 w-4" icon="icon-repair" />,
      key: 'upscale',
      description: '提升图像分辨率和质量',
    },
    {
      name: '今日图片',
      href: '/creation/images',
      icon: <IconFont className="h-4 w-4" icon="icon-images" />,
      key: 'images',
      description: '浏览今日生成的图片',
    },
  ]

  // 检查当前路径是否激活
  const isActive = (item) => {
    if (item.href === '/' && router.asPath === '/') return true
    if (item.href !== '/' && router.asPath.includes(item.key)) return true
    return false
  }

  // 检查AI聊天组是否激活
  const isChatGroupActive = () => {
    return router.asPath.includes('/chat')
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 w-full border-b border-border/30 bg-background/80 supports-[backdrop-filter]:bg-background/80">
        <div className="container flex h-16 items-center justify-between">
          {/* Logo */}
          <motion.div
            className="flex cursor-pointer items-center gap-2.5"
            onClick={() => router.push('/')}
            role="presentation"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="relative overflow-hidden rounded-full border border-primary/10 shadow-sm">
              <Image src="/logo.png" width={36} height={36} alt="Ground AI" className="rounded-full transition-transform hover:scale-105" />
            </div>
            <span className="hidden bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-xl font-bold text-transparent sm:inline-block">Ground AI</span>
          </motion.div>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList className="gap-1">
              {mainMenuItems.map((item) => (
                <NavigationMenuItem key={item.key}>
                  <Button
                    variant={isActive(item) ? 'default' : 'ghost'}
                    size="sm"
                    className={cn('h-9 rounded-full px-4 transition-all duration-200', isActive(item) && 'shadow-sm')}
                    onClick={() => router.push(item.href)}
                  >
                    <span className="flex items-center gap-1.5">
                      {item.icon}
                      <span>{item.name}</span>
                    </span>
                  </Button>
                </NavigationMenuItem>
              ))}

              {/* AI聊天下拉菜单 */}
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={cn(
                    'h-9 gap-1 rounded-full transition-all duration-200',
                    isChatGroupActive() && 'bg-primary text-primary-foreground shadow-sm data-[state=open]:bg-primary/90 data-[state=open]:text-primary-foreground'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <MessageSquare className="h-4 w-4" />
                    <span>AI聊天</span>
                  </span>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <motion.ul initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="grid w-[280px] gap-2 p-3 md:w-[320px]">
                    {chatMenuItems.map((item) => (
                      <motion.li key={item.key} className="row-span-1" whileHover={{ x: 2 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
                        <NavigationMenuLink asChild>
                          <Button
                            variant="ghost"
                            className={cn(
                              'group flex h-auto w-full cursor-pointer flex-row items-center justify-start rounded-lg border border-transparent p-3 text-left transition-all',
                              isActive(item) ? 'border-border/20 bg-muted/80' : 'hover:border-border/10 hover:bg-muted/50'
                            )}
                            onClick={() => router.push(item.href)}
                          >
                            <div
                              className={cn(
                                'mr-3 flex h-9 w-9 items-center justify-center rounded-full transition-colors',
                                isActive(item) ? 'bg-primary/10 text-primary' : 'bg-muted group-hover:bg-primary/5'
                              )}
                            >
                              {item.icon}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium">{item.name}</span>
                              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{item.description}</p>
                            </div>
                          </Button>
                        </NavigationMenuLink>
                      </motion.li>
                    ))}
                  </motion.ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* AI绘画下拉菜单 */}
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={cn(
                    'h-9 gap-1 rounded-full transition-all duration-200',
                    router.asPath.includes('/creation') && 'bg-primary text-primary-foreground shadow-sm data-[state=open]:bg-primary/90 data-[state=open]:text-primary-foreground'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <Paintbrush className="h-4 w-4" />
                    <span>AI绘画</span>
                  </span>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <motion.ul initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="grid w-[280px] gap-2 p-3 md:w-[320px]">
                    {drawingMenuItems.map((item) => (
                      <motion.li key={item.key} className="row-span-1" whileHover={{ x: 2 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
                        <NavigationMenuLink asChild>
                          <Button
                            variant="ghost"
                            className={cn(
                              'group flex h-auto w-full cursor-pointer flex-row items-center justify-start rounded-lg border border-transparent p-3 text-left transition-all',
                              router.asPath === item.href ? 'border-border/20 bg-muted/80' : 'hover:border-border/10 hover:bg-muted/50'
                            )}
                            onClick={() => {
                              if (item.href === '/creation/images' && !getToken()) {
                                viewSet('loginDialogOpen', true)
                              } else {
                                router.push(item.href)
                              }
                            }}
                          >
                            <div
                              className={cn(
                                'mr-3 flex h-9 w-9 items-center justify-center rounded-full transition-colors',
                                router.asPath === item.href ? 'bg-primary/10 text-primary' : 'bg-muted group-hover:bg-primary/5'
                              )}
                            >
                              {item.icon}
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium">{item.name}</span>
                              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{item.description}</p>
                            </div>
                          </Button>
                        </NavigationMenuLink>
                      </motion.li>
                    ))}
                  </motion.ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Mobile Navigation */}
          <div className="flex items-center gap-2 md:hidden">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Menu" className="h-9 w-9 rounded-full">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] border-r-border/30 p-0">
                <div className="flex h-full flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 border-b border-border/10 p-4">
                      <Image src="/logo.png" width={36} height={36} alt="Ground AI" className="rounded-full" />
                      <span className="text-lg font-bold">Ground AI</span>
                    </div>
                    <div className="space-y-1 p-3">
                      {mainMenuItems.map((item) => (
                        <Button
                          key={item.key}
                          variant={isActive(item) ? 'secondary' : 'ghost'}
                          className={cn('mb-1 h-11 w-full justify-start rounded-lg', isActive(item) ? 'bg-primary/10 font-medium text-primary' : '')}
                          onClick={() => {
                            router.push(item.href)
                            setIsMenuOpen(false)
                          }}
                        >
                          <span className="flex items-center gap-3">
                            <span className="flex h-6 w-6 items-center justify-center">{item.icon}</span>
                            <span>{item.name}</span>
                          </span>
                        </Button>
                      ))}

                      {/* Mobile AI聊天分组 */}
                      <div className="mt-2 space-y-1 border-t border-border/10 pt-2">
                        <div className="px-3 py-2 text-xs font-medium text-muted-foreground">AI聊天</div>
                        {chatMenuItems.map((item) => (
                          <Button
                            key={item.key}
                            variant={isActive(item) ? 'secondary' : 'ghost'}
                            className={cn('mb-1 h-11 w-full justify-start rounded-lg', isActive(item) ? 'bg-primary/10 font-medium text-primary' : '')}
                            onClick={() => {
                              router.push(item.href)
                              setIsMenuOpen(false)
                            }}
                          >
                            <span className="flex items-center gap-3">
                              <span className="flex h-6 w-6 items-center justify-center">{item.icon}</span>
                              <span>{item.name}</span>
                            </span>
                          </Button>
                        ))}

                        {/* Mobile AI绘画分组 */}
                        <div className="mt-2 space-y-1 border-t border-border/10 pt-2">
                          <div className="px-3 py-2 text-xs font-medium text-muted-foreground">AI绘画</div>
                          {drawingMenuItems.map((item) => (
                            <Button
                              key={item.key}
                              variant={router.asPath === item.href ? 'secondary' : 'ghost'}
                              className={cn('mb-1 h-11 w-full justify-start rounded-lg', router.asPath === item.href ? 'bg-primary/10 font-medium text-primary' : '')}
                              onClick={() => {
                                if (item.href === '/creation/images' && !getToken()) {
                                  viewSet('loginDialogOpen', true)
                                } else {
                                  router.push(item.href)
                                  setIsMenuOpen(false)
                                }
                              }}
                            >
                              <span className="flex items-center gap-3">
                                <span className="flex h-6 w-6 items-center justify-center">{item.icon}</span>
                                <span>{item.name}</span>
                              </span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-border/10 p-3">
                    <Button
                      variant="ghost"
                      className="h-11 w-full justify-start rounded-lg"
                      onClick={() => {
                        router.push('/settings')
                        setIsMenuOpen(false)
                      }}
                    >
                      <span className="flex items-center gap-3">
                        <Settings className="h-4 w-4" />
                        <span>设置</span>
                      </span>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-2">
            <HeaderDropdown needLogin={need_login} />
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-40 block border-t border-border/20 bg-background/80 supports-[backdrop-filter]:bg-background/80 md:hidden">
        <div className="container flex h-14 items-center justify-between px-0">
          <AnimatePresence mode="wait">
            {[...mainMenuItems, chatMenuItems[0]].map((item) => (
              <motion.div key={item.key} className="flex-1" whileTap={{ scale: 0.92 }} transition={{ type: 'spring', stiffness: 400, damping: 17 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'flex h-14 w-full flex-col items-center justify-center gap-1 rounded-none transition-colors',
                    isActive(item) ? 'text-primary' : 'text-muted-foreground'
                  )}
                  onClick={() => router.push(item.href)}
                >
                  <div className="relative">
                    {item.icon}
                    {isActive(item) && (
                      <motion.div
                        className="absolute -bottom-1 left-1/2 h-1 w-6 -translate-x-1/2 rounded-full bg-primary"
                        layoutId="mobileActiveIndicator"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                  </div>
                  <span className="text-[10px]">{item.name}</span>
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Login Dialog */}
      <Dialog open={view.loginDialogOpen} onOpenChange={(open) => viewSet('loginDialogOpen', open)}>
        <DialogContent className="max-w-[400px] px-6 sm:max-w-[425px]">
          <Auth />
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {process.env.NEXT_PUBLIC_MOCK_MODE === 'true' && (
        <div className="fixed bottom-20 right-4 z-[999] rounded-full bg-amber-400 px-3 py-1 text-xs font-semibold text-amber-900 shadow-lg md:bottom-4">DEMO · Mock 数据</div>
      )}
    </div>
  )
}

export default App
